"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion } from "motion/react";

import annaEmilyChristina from "@/app/(public)/shared/assets/anna_emily_christina.jpg";
import officeWarming from "@/app/(public)/shared/assets/officewarming.png";

export default function Company() {
  const vcLogos = [
    { src: "/landing/hikeventures.webp", name: "Hike Ventures" },
    { src: "/landing/decacorn.png", name: "Decacorn" },
    { src: "/landing/ventureus.png", name: "VentureUs" },
    { src: "/landing/ycombinator.png", name: "Y Combinator" },
    { src: "/landing/underdog_labs.webp", name: "Underdog Labs" },
    { src: "/landing/basis_set_ventures.png", name: "Basis Set Ventures" },
    { src: "/landing/sbxi.svg", name: "SBXi" },
    { src: "/landing/e14.svg", name: "E14" },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-12 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">About Nowadays</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                The go-to platform for event planning, powered by AI to make bringing people together simple and
                seamless.
              </p>
            </div>

            {/* VC Logo Wall */}
            <div className="flex flex-col gap-4 md:text-left text-center">
              <p className="text-body text-landing-secondary">Backed by leading investors like</p>
              <div className="flex items-center justify-center md:justify-start gap-8 flex-wrap">
                {vcLogos.slice(0, 6).map((logo, index) => (
                  <div key={index} className="flex items-center justify-center w-32 h-12">
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className="w-auto h-8 max-w-[120px] object-contain opacity-60 transition-all duration-300 filter grayscale hover:grayscale-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-gradient-to-br from-landing-cta-light/10 to-landing-cta-dark/10 rounded-2xl transform rotate-3"></div>
              <img
                src="/landing/Amy_and_Anna.jpg"
                alt="Amy and Anna - Nowadays Founders"
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            className="flex flex-col gap-8"
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">The Go-to Platform for Event Planning</h2>
              <div className="flex flex-col gap-4 text-body text-landing-secondary">
                <p>
                  The data is clear - people want to meet in person. With increasingly distributed workforces,
                  organizations rely more than ever on events to engage and retain employees, and build team culture.
                  Based on survey results from GBT, 60% of meetings and events this year will be in-person and continue
                  growing. Face-to-face interactions are irreplaceable.
                </p>
                <p>
                  With inflation, staffing shortages, and elevated attendee expectations, it's more important than ever
                  that meeting planners work smarter, not harder. Luckily, this is the first time in history that AI can
                  help automate tedious tasks and help with end-to-end event planning.
                </p>
                <p>
                  Planning corporate events, whether small team meetings or large-scale conferences, is often a
                  logistical nightmare. Managing manual emails, spreadsheets, and callbacks can be incredibly
                  time-consuming and costly.
                </p>
                <p>
                  With Nowadays, our AI can contact venues by email and phone to get availability, deal with
                  back-and-forth negotiations, and present the best options. Our tech scales the impact of human event
                  planners, so they can focus on creating memorable experiences instead of getting bogged down by
                  logistics.
                </p>
                <p>
                  Our mission at Nowadays is to make it simple to bring people together. We want to create a world where
                  the joy of events is never overshadowed by the complexity of planning them, making memorable
                  experiences accessible to everyone, everywhere.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-gradient-to-br from-landing-cta-light/10 to-landing-cta-dark/10 rounded-2xl transform rotate-3"></div>
              <img
                src={annaEmilyChristina.src}
                alt="Amy and Anna - Nowadays Founders"
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 sm:py-16 md:py-24">
        <div className="flex flex-col items-center gap-12">
          <h2 className="text-h2 text-landing-primary text-center">Our Values</h2>

          <div className="flex flex-col gap-6 max-w-3xl mx-auto w-full">
            <div className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col hover:bg-white">
              <div className="flex items-center gap-6">
                <div className="text-4xl flex-shrink-0 w-16 flex justify-center">🔑</div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h4 text-landing-primary">Be the customer's best friend</h3>
                  <p className="text-body text-landing-secondary">
                    We treat customers like close friends. Anticipate needs, go above and beyond, no ego.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col hover:bg-white">
              <div className="flex items-center gap-6">
                <div className="text-4xl flex-shrink-0 w-16 flex justify-center">📣</div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h4 text-landing-primary">Communicate early & clearly</h3>
                  <p className="text-body text-landing-secondary">
                    Speed matters. Clarity matters more. Anticipate questions, prevent problems.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col hover:bg-white">
              <div className="flex items-center gap-6">
                <div className="text-4xl flex-shrink-0 w-16 flex justify-center">🚀</div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h4 text-landing-primary">Raise the bar, then again</h3>
                  <p className="text-body text-landing-secondary">
                    We don't coast. We learn fast, improve constantly, and own the standard.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col hover:bg-white">
              <div className="flex items-center gap-6">
                <div className="text-4xl flex-shrink-0 w-16 flex justify-center">💛</div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h4 text-landing-primary">Stronger as a team</h3>
                  <p className="text-body text-landing-secondary">
                    We stay human, warm, and team-first. We lift each other up and show up fully because we're in this
                    together.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col hover:bg-white">
              <div className="flex items-center gap-6">
                <div className="text-4xl flex-shrink-0 w-16 flex justify-center">🧱</div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h4 text-landing-primary">Own the outcome</h3>
                  <p className="text-body text-landing-secondary">
                    We take initiative, start things, finish things, and take pride in the work we ship.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 sm:py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            className="flex justify-center lg:justify-start"
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-gradient-to-br from-landing-cta-light/10 to-landing-cta-dark/10 rounded-2xl transform rotate-3"></div>
              <img
                src={officeWarming.src}
                alt="Nowadays Team"
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="flex flex-col gap-8"
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">Our Team</h2>
              <div className="flex flex-col gap-4 text-body text-landing-secondary">
                <p>
                  Amy and Anna are sisters who know all too well the hassle of organizing large-scale events. As class
                  presidents at their respective universities, they planned events for over 3,300 students. Anna
                  graduated from MIT and previously worked in PM & SWE roles at Datadog, DoorDash, and Amazon. Amy
                  graduated from Johns Hopkins and formerly worked in strategy and operations roles at Google, Meta, and
                  McKinsey.
                </p>
                <p>
                  The idea of Nowadays came to them while catching up one spring break, realizing that they had both
                  been spending too much time calling and emailing different venues for the upcoming events they were
                  planning (e.g., Amy's wedding, Anna's 1,000+ senior class event). They realized that AI could
                  revolutionize the world of event planning, and they were excited to start a company together to make
                  that change :)
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-12 sm:py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            className="flex flex-col gap-8"
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">Join Us</h2>
              <div className="flex flex-col gap-4 text-body text-landing-secondary">
                <p>
                  We're building the future of event planning and looking for passionate people to join our mission.
                  Help us revolutionize how organizations bring people together through the power of AI.
                </p>
                <p>
                  Whether you're an engineer, designer, marketer, or operations expert, we're always excited to meet
                  talented individuals who want to make event planning simple and seamless for everyone.
                </p>
                <p>
                  Join a fast-growing startup backed by leading investors where you can make a real impact on how
                  millions of events are planned and executed around the world.
                </p>
              </div>
            </div>
            <div className="pt-2">
              <Button variant="primary" color="primary" size="lg" onClick={() => window.open("/careers", "_self")}>
                View Open Positions
              </Button>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-gradient-to-br from-landing-cta-light/10 to-landing-cta-dark/10 rounded-2xl transform rotate-3"></div>
              <img
                src="/landing/nowadays_team.JPG"
                alt="Nowadays Team"
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
